import DescriptionIcon from "@mui/icons-material/Description";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import GridOnIcon from "@mui/icons-material/GridOn";
import { IconButton, ListItemIcon, ListItemText, MenuItem, MenuList, Tooltip } from "@mui/material";
import { memo, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemReactTinyPopover } from "../../Popups/SystemReactTinyPopover";
import type { ExportFormat } from "./exportGridData";

interface GridExportButtonProps {
	onExport: (format: ExportFormat) => void;
	disabled?: boolean;
	dataTestId?: string;
}

/** Toolbar download button: opens a small menu to export the grid as XLSX or CSV. */
export const GridExportButton = memo(function GridExportButton({ onExport, disabled, dataTestId }: GridExportButtonProps) {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);

	const handlePick = useCallback(
		(format: ExportFormat) => {
			setIsOpen(false);
			onExport(format);
		},
		[onExport],
	);

	return (
		<SystemReactTinyPopover
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			positions={["bottom", "top", "left", "right"]}
			align="end"
			content={
				<MenuList disablePadding>
					<MenuItem onClick={() => handlePick("xlsx")} data-testid={dataTestId ? `${dataTestId}-xlsx` : undefined}>
						<ListItemIcon>
							<GridOnIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText primary={t("system.dataGrid.exportXlsx")} />
					</MenuItem>
					<MenuItem onClick={() => handlePick("csv")} data-testid={dataTestId ? `${dataTestId}-csv` : undefined}>
						<ListItemIcon>
							<DescriptionIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText primary={t("system.dataGrid.exportCsv")} />
					</MenuItem>
				</MenuList>
			}
		>
			<Tooltip title={t("system.dataGrid.export")}>
				{/* span so the tooltip still works while the button is disabled */}
				<span>
					<IconButton
						size="small"
						aria-label={t("system.dataGrid.export")}
						onClick={() => setIsOpen((prev) => !prev)}
						disabled={disabled}
						data-testid={dataTestId}
					>
						<FileDownloadIcon fontSize="small" />
					</IconButton>
				</span>
			</Tooltip>
		</SystemReactTinyPopover>
	);
});
